import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/** A small "i" affordance that reveals a soft explanatory bubble on hover,
 *  keyboard focus, or click (click pins it open). The bubble is portalled to
 *  <body> so it never gets clipped by a table's or card's overflow. */
export default function InfoTip({
  label,
  children,
  width = 264,
}: {
  label: string;
  children: ReactNode;
  width?: number;
}) {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const open = hover || pinned;

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      const m = 8;
      let left = b.left + b.width / 2 - width / 2;
      left = Math.max(m, Math.min(left, window.innerWidth - width - m));
      setPos({ top: b.bottom + 6, left });
    };
    place();
    const dismiss = () => {
      setHover(false);
      setPinned(false);
    };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open, width]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHover(false);
        setPinned(false);
      }
    };
    const onDown = (e: MouseEvent) => {
      if (!btnRef.current?.contains(e.target as Node)) {
        setHover(false);
        setPinned(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <span className="infotip">
      <button
        ref={btnRef}
        type="button"
        className="infotip-btn"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPinned((p) => !p);
        }}
      >
        i
      </button>
      {open &&
        pos &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            className="infotip-bubble"
            style={{ top: pos.top, left: pos.left, width }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {children}
          </span>,
          document.body,
        )}
    </span>
  );
}
